import type { ProjectStatusKey, ProjectStatusRef } from "@/types/api/projectStatuses";

export type ProjectTypeRef = {
  id: string;
  name: string;
  defaultServiceFrontModelId: string | null;
  defaultServiceFrontModel?: ProjectServiceFrontModelRef | null;
};

export type ProjectPriorityRef = {
  id: string;
  name: string;
  color: string;
};

export type { ProjectStatusKey, ProjectStatusRef };

export type ProjectResponsibleWorkerRef = {
  id: string;
  name: string;
  code?: string;
};

export type ProjectServiceFrontModelRef = {
  id: string;
  name: string;
};

export type ProjectDto = {
  id: string;
  code: string;
  title: string;
  description: string | null;
  client: string;
  clientPartnerId: string;
  clientPartnerCode?: string;
  plannedRevenue?: number | null;
  address: string | null;
  zipCode: string | null;
  street: string | null;
  streetNumber: string | null;
  complement: string | null;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
  ibgeMunicipalityCode: string | null;
  startDate: string;
  projectType: ProjectTypeRef;
  priority: ProjectPriorityRef;
  responsibleWorker: ProjectResponsibleWorkerRef;
  status: ProjectStatusRef;
  isActive: boolean;
  progress: number;
  dueDate: string | null;
  defaultDiaryReportModelRef: string;
  serviceFrontModel?: ProjectServiceFrontModelRef | null;
  serviceFrontModelId?: string | null;
  createdAt: string;
};

export type ProjectCreateBody = {
  title: string;
  code: string;
  clientPartnerId: string;
  startDate: string;
  description?: string | null;
  zipCode?: string | null;
  street?: string | null;
  streetNumber?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  ibgeMunicipalityCode?: string | null;
  projectTypeId?: string;
  priorityId: string;
  responsibleWorkerId: string;
  statusKey: ProjectStatusKey;
  dueDate?: string | null;
  plannedRevenue?: number | null;
  serviceFrontModelId?: string | null;
  tasks?: ProjectImportMsProjectTask[];
  saveAsModel?: boolean;
  serviceFrontModelName?: string | null;
};

export type ProjectNextCodeDto = {
  code: string;
};

export type ProjectImportMsProjectTask = {
  name: string;
  start: string;
  finish: string;
};

export type ProjectImportMsProjectPreviewResult = {
  tasks: ProjectImportMsProjectTask[];
};

export type ProjectPatchBody = Partial<
  Omit<ProjectCreateBody, "tasks" | "saveAsModel" | "serviceFrontModelName">
>;

export type ProjectsPage = {
  items: ProjectDto[];
  nextCursor: string | null;
  total: number;
};

export type ProjectsListParams = {
  limit?: number;
  cursor?: string;
  q?: string;
  /** API fields as `name:asc,progress:desc`. */
  sort?: string;
  progressFrom?: number;
  progressTo?: number;
  statusKeys?: string[];
  priorityIds?: string[];
  responsibleWorkerIds?: string[];
  clients?: string[];
  startDateFrom?: string;
  startDateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  active?: boolean;
};

export type ProjectFilterOptionType = "status" | "priority" | "responsible" | "client";

export type ProjectFilterOptionItem = {
  id: string;
  name: string;
  key?: string;
  color?: string;
};

export type ProjectFilterOptionsPage = {
  items: ProjectFilterOptionItem[];
  nextCursor: string | null;
};

export type ProjectFilterOptionsParams = {
  type: ProjectFilterOptionType;
  q?: string;
  limit?: number;
  cursor?: string;
  ids?: string[];
};

export const PROJECTS_PAGE_SIZE = 25;

export const PROJECT_STATUS_KEY_VALUES = [
  "planned",
  "in_progress",
  "on_hold",
  "overdue",
  "completed",
  "cancelled",
] as const satisfies readonly ProjectStatusKey[];
